import { useState } from "react";
import { Table, Tag, Space, Tooltip, Typography, Button, Popconfirm } from "antd";
import { EditOutlined, DeleteOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

const { Text } = Typography;

export default function OrdenesServicioPOTable({ data, loading, onEdit, onDelete, permissionDelete, permissionUpdate }) {
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10
  });

  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys) => {
      setSelectedRowKeys(keys);
    }
  };

  const handleDeleteSelected = async () => {
    for (const id of selectedRowKeys) {
      await onDelete({ oid: id });
    }

    setSelectedRowKeys([]);
  };
  const formatNumber = (num) =>
    new Intl.NumberFormat("es-CO").format(num);

  const formatMoney = (num) =>
    new Intl.NumberFormat("es-CO", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(num);

  const estadoMap = {
    "finally closed": { text: "Entregado", color: "green" },
    "closed": { text: "Cerrado", color: "green" },
    "open": { text: "Pendiente", color: "orange" },
    "pending": { text: "Pendiente", color: "orange" }
  };

  const columns = [
    {
      title: "PO",
      dataIndex: "Numero_PO",
      width: 110,
      render: (value) => <Text strong>{value}</Text>
    },
    {
      title: "Producto",
      width: 320,
      render: (_, record) => (
        <Tooltip
          title={
            <>
              <div>{record.nombre_producto}</div>
              <div style={{ marginTop: 4, color: "#999" }}>{record.tipo_producto}</div>
            </>
          }
        >
          <div style={{ maxWidth: 320 }}>
            <Text strong style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
              {record.nombre_corto || record.nombre_producto}
            </Text>
            <Text type="secondary" style={{ fontSize: 12, display: "-webkit-box", WebkitLineClamp: 1, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
              {record.tipo_producto}
            </Text>
          </div>
        </Tooltip>
      )
    },
    {
      title: "Proveedor",
      width: 260,
      render: (_, record) => (
        <Tooltip title={record.nombre}>
          <div style={{ maxWidth: 260 }}>
            <Text style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
              {record.nombre}
            </Text>
          </div>
        </Tooltip>
      )
    },
    {
      title: "Stock",
      width: 110,
      align: "center",
      render: (_, record) => {
        const stock = Number(record.stock_disponible || 0);
        let color = "green";
        if (stock === 0) color = "orange";
        if (stock < 0) color = "red";
        return <Tag color={color}>{formatNumber(stock)} {record.unidad_medida}</Tag>;
      }
    },
    {
      title: "Estado inventario",
      width: 130,
      align: "center",
      render: (_, record) => {
        const estado = record.estado_stock;
        let color = "green";
        if (estado === "CRITICO") color = "red";
        if (estado === "ENTREGADO") color = "blue";
        return <Tag color={color}>{estado}</Tag>;
      }
    },
    {
      title: "Valor COP",
      width: 130,
      align: "right",
      render: (_, record) => <Text strong>$ {formatMoney(record.valor_unitario_COP)}</Text>
    },
    {
      title: "Entrega estimada",
      width: 130,
      render: (_, record) => (
        <Text>{record.fecha_estimada_entrega ? dayjs(record.fecha_estimada_entrega).format("DD/MM/YYYY") : ""}</Text>
      )
    },
    {
      title: "Estado",
      width: 120,
      render: (_, record) => {
        const estado = record.estado?.toLowerCase();
        const config = estadoMap[estado] || { text: record.estado, color: "default" };
        return <Tag color={config.color}>{config.text}</Tag>;
      }
    },
    {
      title: "Acciones",
      width: 90,
      align: "center",
      render: (_, record) => (
        <Space>
          {permissionUpdate && (
            <Tooltip title="Editar">
              <Button size="small" icon={<EditOutlined />} onClick={() => onEdit(record)} />
            </Tooltip>
          )}
          {permissionDelete && (
            <Popconfirm title="¿Eliminar orden/producto?" okText="Eliminar" cancelText="Cancelar" onConfirm={() => onDelete(record)}>
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          )}
        </Space>
      )
    }
  ];

  return (
    <div>
      {permissionDelete && selectedRowKeys.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <Popconfirm title={`¿Eliminar ${selectedRowKeys.length} registros?`} onConfirm={handleDeleteSelected} okText="Eliminar" cancelText="Cancelar">
            <Button danger>Eliminar seleccionados ({selectedRowKeys.length})</Button>
          </Popconfirm>
        </div>
      )}
      <Table
        rowKey="oid"
        columns={columns}
        dataSource={data}
        loading={loading}
        rowSelection={permissionDelete ? rowSelection : null}
        bordered
        size="middle"
        scroll={{ x: 1300 }}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "30"],
          showTotal: (total) => `Total ${total} registros`
        }}
        onChange={(pager) => setPagination({ current: pager.current, pageSize: pager.pageSize })}
        style={{ borderRadius: 10, overflow: "hidden" }}
      />
    </div>
  );
}
